import { createContext, useContext } from 'react'
import { can, type Capability } from '@/shared/domain/rbac'
import type { Role } from '@/shared/domain/types'

/// Хто зараз за касою і що йому дозволено.
///
/// Контекст лежить окремо від провайдера, щоб імпорт хука не тягнув у граф
/// модуля жодного запиту — і щоб Fast Refresh не ламався на провайдері.

export type Staff = {
  id: string
  name: string
  role: Role
  /// Філія, у якій відкрита зміна. Мультифілійність — R2, але поле є одразу:
  /// додати його пізніше означає переписати кожен запит.
  branchId: string
  branchName: string
}

export type SessionValue = {
  staff: Staff | null
  /// Правило екрана: недоступна дія не показується взагалі. Це предикат для
  /// «показувати чи ні», а не для «блокувати чи ні».
  allows: (capability: Capability) => boolean
  /// РЕЖИМ РОЗРОБКИ: примірити іншу роль.
  ///
  /// Автентифікації ще немає, роль приходить із заглушки — і поки вона була
  /// сталим `manager`, половина продукту була недосяжна для перевірки: усі
  /// гілки «🔒 покличте менеджера», усі підписи неактивних дій і весь вигляд
  /// екрана очима рецепції чи тренера. Права ухвалювались рішеннями (D6), а
  /// подивитись на них не було як — тому вони й розʼїхались із реалізацією.
  ///
  /// Це не «перемикач ролей» продукту: справжня роль прийде від сервера разом
  /// із сесією, і цей виклик зникне разом із заглушкою.
  actAs: (role: Role) => void
  signOut: () => void
}

export const SessionContext = createContext<SessionValue | null>(null)

export const useSession = (): SessionValue => {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession must be used inside <SessionProvider>')
  return session
}

/// Персонал за касою, для екранів, які без нього не існують. Кидати тут —
/// помилка програміста, не користувача: гейт у layout не пускає далі без зміни.
export const useStaff = (): Staff => {
  const { staff } = useSession()
  if (!staff) throw new Error('useStaff used outside an authenticated route')
  return staff
}

export const allowsFor = (role: Role, capability: Capability): boolean => can(role, capability)
